import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * Phase 6am (Hotfix Z.6) — live channel-counters hook.
 *
 * Subscribes to `/api/live/counters` and re-polls every `pollMs` ms
 * (default 5s) so any consuming list (Trunks, Customers, etc.) shows
 * channel usage in real time without having to refresh the page.
 *
 * Pre-Z.6 this was a one-shot fetch indexed by an opaque `dep`. That
 * worked for static snapshots but operators had to navigate away and
 * back to see "Trunk T1 is now at 9/10 out". Live polling closes that
 * loop without resorting to a per-list WebSocket.
 *
 * Returned shape (one entry per `scope:scope_id` key):
 *   {
 *     total, in, out,                    // current in-flight
 *     cap_total, cap_in, cap_out,        // configured caps (0 = uncapped)
 *     channel_policy, name               // server-supplied for convenience
 *   }
 *
 * Callers that previously read `{total, in, out}` still work because
 * those keys are unchanged.
 */
export function useCounters(dep, { pollMs = 5000 } = {}) {
  const [map, setMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    let itv = null;
    const pull = () => {
      api
        .get("/live/counters")
        .then((r) => {
          if (cancelled) return;
          const m = {};
          for (const c of r.data || []) {
            m[`${c.scope}:${c.scope_id}`] = {
              total: c.total ?? c.value ?? 0,
              in: c.in ?? 0,
              out: c.out ?? 0,
              cap_total: c.cap_total ?? 0,
              cap_in: c.cap_in ?? 0,
              cap_out: c.cap_out ?? 0,
              channel_policy: c.channel_policy || "total",
              name: c.name || "",
            };
          }
          setMap(m);
        })
        .catch(() => {
          // Don't reset on a single transient failure — keep last-good
          // values visible. Only clear when the dep changes.
        });
    };
    pull();
    itv = setInterval(pull, pollMs);
    return () => {
      cancelled = true;
      if (itv) clearInterval(itv);
    };
  }, [dep, pollMs]);
  return map;
}

export function counterFor(map, scope, scopeId) {
  return (
    map[`${scope}:${scopeId}`] || {
      total: 0,
      in: 0,
      out: 0,
      cap_total: 0,
      cap_in: 0,
      cap_out: 0,
      channel_policy: "total",
      name: "",
    }
  );
}

/**
 * Props for ChannelMeter derived from a tenant (or trunk) doc + live counters.
 *
 * Prefers server-supplied caps (from the counter row) over the entity's
 * own `limits.*` so the meter reflects exactly what the engine is
 * enforcing — useful when the entity doc has stale caps cached and the
 * counters row is the source of truth.
 */
export function meterPropsFromTenant(tenant, counters, scope) {
  const c = counterFor(counters, scope, tenant.id);
  const lim = tenant.limits || {};
  return {
    policy: c.channel_policy || tenant.channel_policy || "total",
    used: c.total,
    total: c.cap_total || Number(lim.channels || 0),
    used_in: c.in,
    used_out: c.out,
    max_in: c.cap_in || Number(lim.channels_in || 0),
    max_out: c.cap_out || Number(lim.channels_out || 0),
  };
}

export function meterPropsFromTrunk(trunk, counters) {
  const c = counterFor(counters, "trunk", trunk.id);
  return {
    policy: c.channel_policy || trunk.channel_policy || "total",
    used: c.total,
    total: c.cap_total || Number(trunk.channels || 0),
    used_in: c.in,
    used_out: c.out,
    max_in: c.cap_in || Number(trunk.channels_in || 0),
    max_out: c.cap_out || Number(trunk.channels_out || 0),
  };
}
